import React, { useEffect, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { AlertTriangleIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, Loader2Icon, RocketIcon, SaveIcon, XIcon } from 'lucide-react';
import { askCarli } from '../utils/carliAgent';
import { CarliStars } from './CarliStars';
import { Breakdown, ColumnField, UploadAnalysis } from '../utils/uploadAnalysis';
import { CarliReport, requestCarliReport } from '../utils/carliRecoveryReport';
import { CarliReportSections } from './CarliReportSections';
import { ProductNote } from './ProductNotes';
const mappableFields: {
  id: ColumnField;
  label: string;
}[] = [{
  id: 'units',
  label: 'Units'
}, {
  id: 'msrp',
  label: 'MSRP'
}, {
  id: 'name',
  label: 'Product'
}, {
  id: 'category',
  label: 'Category'
}, {
  id: 'size',
  label: 'Size'
}, {
  id: 'sku',
  label: 'SKU'
}, {
  id: 'image',
  label: 'Image'
}];
const currency = (value: number) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
export function UploadAnalysisReport({
  analysis,
  condition,
  indexPrice,
  channelCount,
  productNotes = [],
  channelSummary,
  marketSummary,
  permissionSummary,
  portalContext,
  onRemapColumn,
  onClose,
  onGoLive,
  onSave
}: {
  analysis: UploadAnalysis;
  condition: string;
  indexPrice: number;
  channelCount: number;
  productNotes?: ProductNote[];
  channelSummary?: string;
  marketSummary?: string;
  permissionSummary?: string;
  portalContext?: string;
  onRemapColumn?: (field: ColumnField, column: string) => void;
  onClose: () => void;
  onGoLive: () => void;
  onSave: () => void;
}) {
  const reduceMotion = useReducedMotion();
  const topCategory = analysis.categories[0];

  // Deterministic summary of the parsed file, used as Carli's briefing and as
  // the fallback if the agent is unreachable.
  const computedRead = useMemo(() => `This lot carries ${analysis.totalUnits.toLocaleString()} units at a weighted MSRP of ${currency(analysis.weightedMsrp)}.` + (topCategory ? ` ${topCategory.label} is the largest group at ${Math.round(topCategory.share)}% of units.` : '') + ` Graded as ${condition.toLowerCase()} and capped at ${indexPrice}% off MSRP, expected recovery is ${currency(analysis.expectedRecovery)} across ${channelCount || 'the approved'} ${channelCount === 1 ? 'channel' : 'channels'} — roughly ${currency(analysis.recoveryPerUnit)} per unit.` + (analysis.gaps.length > 0 ? ` Confidence sits at ${analysis.confidence}% because ${analysis.gaps[0].count.toLocaleString()} lines are ${analysis.gaps[0].label.replace('Lines missing ', 'missing ')}.` : ` Data coverage is complete, which is why confidence sits at ${analysis.confidence}%.`), [analysis, condition, indexPrice, channelCount, topCategory]);
  const [read, setRead] = useState(computedRead);
  const [readState, setReadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [report, setReport] = useState<CarliReport | null>(null);
  const notesKey = productNotes.map(note => `${note.product}:${note.note}`).join('|');
  const [lotProfile, setLotProfile] = useState('');
  const [chat, setChat] = useState<{
    role: 'user' | 'assistant';
    content: string;
  }[]>([]);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const ask = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || asking) return;
    const history = [...chat, {
      role: 'user' as const,
      content: trimmed
    }];
    setChat(history);
    setQuestion('');
    setAsking(true);
    const lotContext = `UPLOADED LOT UNDER REVIEW\n${lotProfile}`;
    try {
      let reply: string;
      try {
        reply = await askCarli({
          messages: history,
          context: [portalContext, lotContext].filter(Boolean).join('\n\n'),
          maxTokens: 900
        });
      } catch {
        // Fall back to the lot on its own if the full workspace briefing is too
        // large for the request to succeed.
        reply = await askCarli({
          messages: history,
          context: lotContext,
          maxTokens: 900
        });
      }
      setChat([...history, {
        role: 'assistant',
        content: reply
      }]);
    } catch (error) {
      const detail = error instanceof Error ? error.message : '';
      setChat([...history, {
        role: 'assistant',
        content: detail || 'Carli could not respond. Please try again.'
      }]);
    } finally {
      setAsking(false);
    }
  };

  // Hand the parsed profile to Carli so the interpretation is genuinely written
  // by the agent rather than assembled from a template.
  useEffect(() => {
    let cancelled = false;
    const profile = [`File: ${analysis.fileName}`, `Lines: ${analysis.rowCount.toLocaleString()} · Units: ${analysis.totalUnits.toLocaleString()}`, `Condition: ${condition}`, `Total MSRP: ${currency(analysis.totalMsrp)} · Weighted MSRP per unit: ${currency(analysis.weightedMsrp)}`, `Modeled recovery rate: ${Math.round(analysis.recoveryRate * 100)}% of MSRP`, `Expected recovery: ${currency(analysis.expectedRecovery)} (conservative ${currency(analysis.conservativeRecovery)}, upside ${currency(analysis.upsideRecovery)})`, `Recovery per unit: ${currency(analysis.recoveryPerUnit)} · Marketing allocation: ${currency(analysis.marketingAllocation)}`, `Public pricing limit: ${indexPrice}% off MSRP`, channelSummary ? `Approved channels: ${channelSummary}` : `Approved channels: ${channelCount}`, marketSummary ? `Markets: ${marketSummary}` : '', permissionSummary ? `Promotion permissions: ${permissionSummary}` : '', `Data confidence: ${analysis.confidence}%`, `Categories: ${analysis.categories.map(row => `${row.label} ${row.units.toLocaleString()} units (${Math.round(row.share)}%)`).join('; ')}`, `Sizes: ${analysis.sizes.map(row => `${row.label} ${Math.round(row.share)}%`).join('; ')}`, `MSRP bands: ${analysis.priceBands.map(row => `${row.label} ${Math.round(row.share)}%`).join('; ')}`, analysis.gaps.length > 0 ? `Data gaps: ${analysis.gaps.map(gap => `${gap.count.toLocaleString()} ${gap.label.toLowerCase()}`).join('; ')}` : 'Data gaps: none', analysis.skippedRows > 0 ? `${analysis.skippedRows.toLocaleString()} image-only rows were excluded (Shopify writes one row per additional photo). Do not treat these as missing data.` : '', productNotes.length > 0 ? `Brand notes: ${productNotes.map(note => `${note.product} — ${note.note}`).join('; ')}` : ''].filter(Boolean).join('\n');
    setLotProfile(profile);
    setReadState('loading');
    requestCarliReport({
      profile,
      portalContext
    }).catch(() => requestCarliReport({
      profile
    })).then(response => {
      if (cancelled) return;
      setReport(response);
      setReadState('ready');
    }).catch(() => {
      if (cancelled) return;
      setRead(computedRead);
      setReadState('error');
    });
    return () => {
      cancelled = true;
    };
    // Notes are compared by content so a new array identity does not re-run the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysis, condition, indexPrice, channelCount, notesKey, computedRead, portalContext, channelSummary, marketSummary, permissionSummary]);
  return <div className="fixed inset-0 z-[80] flex bg-ink/45 p-0 backdrop-blur-sm sm:items-center sm:justify-center sm:p-6" role="dialog" aria-modal="true" aria-labelledby="upload-report-heading" data-id="element-3110" data-mp="1ur37kze4x1d8">
      <button type="button" className="absolute inset-0 cursor-default" onClick={onClose} aria-label="Close analysis report" data-id="element-3111" data-mp="23rrllu3j31q4" />
      <motion.section initial={reduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      scale: 0.96,
      y: 18
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} transition={{
      duration: 0.26,
      ease: [0.23, 1, 0.32, 1]
    }} className="relative z-10 flex min-h-[100dvh] w-full max-w-5xl flex-col overflow-hidden bg-paper shadow-2xl sm:min-h-0 sm:max-h-[92vh] sm:rounded-3xl" data-id="element-3112" data-mp="1zq6aad6n6vi1">
        <header className="border-b border-ink/10 bg-paper px-5 py-4 sm:px-7" data-id="element-3113" data-mp="yvwt5jpy3zfx">
          <div className="flex items-start justify-between gap-4" data-id="element-3114" data-mp="13n53chz3nou5">
            <div className="min-w-0" data-id="element-3115" data-mp="13ny7db6oc08l">
              <button type="button" onClick={onClose} className="-ml-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold text-ink/55 transition-colors hover:bg-cream hover:text-ink" data-id="element-3116" data-mp="17oi3qcjfazto">
                <ChevronLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-3117" data-mp="fzf1f2b3jzjo" />
                Back to setup
              </button>
              <p className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-3118" data-mp="oys1j4dbzbrq">
                <CarliStars data-id="element-3119" data-mp="1vdj73ldefks3" />
                Carli Analysis
              </p>
              <h1 id="upload-report-heading" className="mt-1 truncate font-display text-xl font-medium tracking-tight" data-id="element-3120" data-mp="20cmevdkrk53r">
                {analysis.fileName}
              </h1>
              <p className="mt-0.5 text-xs text-ink/55" data-id="element-3121" data-mp="25i9c6043n50j">
                {analysis.rowCount.toLocaleString()} lines · {analysis.totalUnits.toLocaleString()} units · {condition}
              </p>
              {analysis.mapping.units && <p className="mt-0.5 text-[11px] text-ink/45" data-id="element-3122" data-mp="1wow9w7k5ar1s">
                  Units read from &ldquo;{analysis.mapping.units}&rdquo;
                </p>}
            </div>
            <button type="button" onClick={onClose} className="rounded-lg p-2 text-ink/45 hover:bg-cream hover:text-ink" aria-label="Close" data-id="element-3123" data-mp="1mri6b8jblwqw">
              <XIcon className="h-5 w-5" data-id="element-3124" data-mp="1hurhnwtg9stv" />
            </button>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-3125" data-mp="1dso492n80e5y">
            <button type="button" onClick={onGoLive} className="inline-flex items-center gap-2 rounded-full bg-[#BB5E40] px-5 py-2.5 text-sm font-semibold text-cream transition-colors hover:bg-[#94452E]" data-id="element-3126" data-mp="3c77tdd50lz64">
              <RocketIcon className="h-4 w-4" aria-hidden="true" data-id="element-3127" data-mp="2gumu4rdpje9t" />
              Go live with products
            </button>
            <button type="button" onClick={onSave} className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-sm font-semibold text-ink/70 transition-colors hover:bg-cream" data-id="element-3128" data-mp="2qqpt5od9iwsj">
              <SaveIcon className="h-4 w-4" aria-hidden="true" data-id="element-3129" data-mp="1y07tkxbqzdjv" />
              Save analysis for later
            </button>
            <span className="text-xs text-ink/50" data-id="element-3130" data-mp="27s3u0f1b3fr9">Nothing is activated until you choose.</span>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto bg-cream/40 px-5 py-6 sm:px-7" data-id="element-3131" data-mp="2313vj5cjgpmn">
          {readState === 'loading' && <section className="rounded-2xl border border-[#BB5E40]/25 bg-[#BB5E40]/[0.05] p-6 sm:p-8" role="status" data-id="element-3132" data-mp="1fa87mjb7eeko">
              <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-3133" data-mp="1aalwqgiulaf1">
                <CarliStars data-id="element-3134" data-mp="3qqrz78qac09u" />
                Carli Analysis
              </p>
              <p className="mt-3 flex items-center gap-2 text-sm text-ink/70" data-id="element-3135" data-mp="3qsuzqmp2dbst">
                <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-3136" data-mp="3d0ybitqinm1p" />
                Carli is analyzing {analysis.totalUnits.toLocaleString()} units across {analysis.rowCount.toLocaleString()} lines against your realized sales history…
              </p>
              <p className="mt-2 text-xs text-ink/50" data-id="element-3137" data-mp="dki3p5chiv4b">This takes a few moments. She is building the full recovery report.</p>
            </section>}

          {report && <CarliReportSections report={report} data-id="element-3138" data-mp="4jjy4imeefjm" />}

          {readState === 'error' && <section className="rounded-2xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-6" aria-labelledby="forecast-heading" data-id="element-3139" data-mp="4lem65syunrx">
              <div className="flex flex-wrap items-baseline justify-between gap-3" data-id="element-3140" data-mp="2pac3v3kfc3s8">
                <h2 id="forecast-heading" className="text-sm font-semibold" data-id="element-3141" data-mp="utawc5cm8por">Estimated recovery</h2>
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${analysis.confidence >= 50 ? 'bg-positive/12 text-positive' : 'bg-[#BB5E40]/12 text-[#94452E]'}`} data-id="element-3142" data-mp="purb675ujhxg">
                  Confidence · {analysis.confidence}%
                </span>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-3" data-id="element-3143" data-mp="h0cuc05o3zc8">
                <Outlook label="Conservative" value={analysis.conservativeRecovery} note="Slower sell-through, deeper discounting" data-id="element-3144" data-mp="c4nurwtneifx" />
                <Outlook label="Expected" value={analysis.expectedRecovery} note={`${Math.round(analysis.recoveryRate * 100)}% of MSRP recovered`} highlight data-id="element-3145" data-mp="76cjdhnpfzvl" />
                <Outlook label="Upside" value={analysis.upsideRecovery} note="Strong demand across approved channels" data-id="element-3146" data-mp="3ua0s7bs4tdp7" />
              </div>
              <dl className="mt-5 grid gap-4 border-t border-ink/10 pt-5 sm:grid-cols-4" data-id="element-3147" data-mp="1wglfzzj58a1a">
                <Stat label="Total MSRP" value={currency(analysis.totalMsrp)} data-id="element-3148" data-mp="1sf8cvx7fqky2" />
                <Stat label="Recovery per unit" value={currency(analysis.recoveryPerUnit)} data-id="element-3149" data-mp="3aefyjm0pqhs5" />
                <Stat label="Weighted MSRP" value={currency(analysis.weightedMsrp)} data-id="element-3150" data-mp="3fchie95yql4o" />
                <Stat label="Marketing allocation" value={currency(analysis.marketingAllocation)} data-id="element-3151" data-mp="3k628lf1beatb" />
              </dl>
              <p className="mt-4 border-t border-ink/10 pt-4 text-xs leading-relaxed text-ink/55" data-id="element-3152" data-mp="3o5uh5rqpkgyo">
                {read}
              </p>
              <p className="mt-2 text-[11px] text-ink/45" data-id="element-3153" data-mp="2wewtj2377mk2">
                Carli&rsquo;s full report is unavailable right now, so these figures were modeled from the file directly.
              </p>
            </section>}

          {productNotes.length > 0 && <section className="mt-5 rounded-2xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-6" aria-labelledby="notes-heading" data-id="element-3154" data-mp="35ds0whhzb2oy">
              <h2 id="notes-heading" className="text-sm font-semibold" data-id="element-3155" data-mp="kp49z9je3xve">Your notes on specific products</h2>
              <ul className="mt-4 space-y-2" data-id="element-3156" data-mp="j7yn58de8x26">
                {productNotes.map(note => <li key={note.id} className="rounded-xl border border-ink/10 bg-cream/40 p-3.5" data-id="element-3157" data-mp="e9x0yeko8hmh">
                    <p className="text-xs font-semibold text-ink" data-id="element-3158" data-mp="s9odeavjhpyd">{note.product}</p>
                    <p className="mt-1 text-xs leading-relaxed text-ink/65" data-id="element-3159" data-mp="o7tax31vh6j0">{note.note}</p>
                  </li>)}
              </ul>
              <p className="mt-3 text-[11px] text-ink/50" data-id="element-3160" data-mp="125rnq40jouwe">Carli applied these when recommending channels, pricing, and creative.</p>
            </section>}

          <section className="mt-5 rounded-2xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-6" aria-labelledby="inventory-breakdown-heading" data-id="element-3161" data-mp="1bxnltf2msl0m">
            <h2 id="inventory-breakdown-heading" className="text-sm font-semibold" data-id="element-3162" data-mp="16yuigfh26ae6">Inventory breakdown</h2>
            <dl className="mt-4 grid gap-4 sm:grid-cols-4" data-id="element-3163" data-mp="1kvr9agx6frv2">
              <Stat label="Total units" value={analysis.totalUnits.toLocaleString()} data-id="element-3164" data-mp="1fzsd475lx5k3" />
              <Stat label="Predicted AUR" value={currency(report ? report.headline.aur : analysis.recoveryPerUnit)} data-id="element-3165" data-mp="1zk9dvtme8h7c" />
              <Stat label="Predicted gross recovery" value={currency(report ? report.headline.expected : analysis.expectedRecovery)} data-id="element-3166" data-mp="24iay4hvk2mke" />
              <Stat label="Weighted average MSRP" value={currency(analysis.weightedMsrp)} data-id="element-3167" data-mp="1qlxileal0v8k" />
            </dl>
          </section>

          <div className="mt-5 grid gap-5 lg:grid-cols-2" data-id="element-3168" data-mp="1gub6zguc0u5a">
            <BreakdownCard title="Category mix" rows={analysis.categories} data-id="element-3169" data-mp="1gsqa3kkjoz49" />
            <BreakdownCard title="Size distribution" rows={analysis.sizes} data-id="element-3170" data-mp="1lljh6f3hkjji" />
            <BreakdownCard title="MSRP bands" rows={analysis.priceBands} data-id="element-3171" data-mp="16wn1pwz3o48v" />

            <section className="rounded-2xl border border-ink/10 bg-paper p-5 shadow-sm" aria-labelledby="gaps-heading" data-id="element-3172" data-mp="1buovbbu5jlyl">
              <h2 id="gaps-heading" className="text-sm font-semibold" data-id="element-3173" data-mp="xuxb2td1l9c5">Data quality</h2>
              {analysis.skippedRows > 0 && <p className="mt-3 text-[11px] leading-relaxed text-ink/55" data-id="element-3174" data-mp="12tql2bt4ei7k">
                  {analysis.skippedRows.toLocaleString()} image-only rows were skipped — Shopify writes an extra row per
                  additional photo, so they carry no inventory and are not counted as gaps.
                </p>}
              {analysis.gaps.length === 0 ? <p className="mt-4 flex items-center gap-2 text-xs font-medium text-positive" data-id="element-3175" data-mp="2qt2ke01mjzol">
                  <CheckCircle2Icon className="h-4 w-4" aria-hidden="true" data-id="element-3176" data-mp="3nh8uh2hbfka9" />
                  Every line carries the fields Carli needs.
                </p> : <ul className="mt-4 space-y-3" data-id="element-3177" data-mp="3ehdgyr0afd72">
                  {analysis.gaps.map(gap => <li key={gap.label} className="flex gap-2.5" data-id="element-3178" data-mp="39islt5zw7jph">
                      <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#BB5E40]" aria-hidden="true" data-id="element-3179" data-mp="39kmy5vx3m3hy" />
                      <span className="min-w-0" data-id="element-3180" data-mp="1y3di7jd6htle">
                        <span className="block text-xs font-semibold text-ink" data-id="element-3181" data-mp="1y2kjn5odiaec">
                          {gap.count.toLocaleString()} · {gap.label}
                        </span>
                        <span className="mt-0.5 block text-[11px] leading-relaxed text-ink/55" data-id="element-3182" data-mp="ijmo401kpvga">{gap.detail}</span>
                      </span>
                    </li>)}
                </ul>}
              <div className="mt-5 border-t border-ink/10 pt-4" data-id="element-3183" data-mp="rdj2utses5lo">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-ink/45" data-id="element-3184" data-mp="wccai5l0l2dx">Columns Carli read</p>
                <p className="mt-1.5 text-[11px] leading-relaxed text-ink/55" data-id="element-3185" data-mp="119ul00eojpo1">
                  Change any of these if Carli picked the wrong column — the report recalculates immediately.
                </p>
                <ul className="mt-3 space-y-2" data-id="element-3186" data-mp="1aaam537yakpq">
                  {mappableFields.map(field => <li key={field.id} className="flex items-center gap-2" data-id="element-3187" data-mp="382olc5gd4rta">
                      <span className="w-20 shrink-0 text-[11px] font-semibold text-ink/65" data-id="element-3188" data-mp="3c26wi9idtoix">{field.label}</span>
                      <div className="relative min-w-0 flex-1" data-id="element-3189" data-mp="2t5ol9w9oxhbp">
                        <select value={analysis.mapping[field.id] ?? ''} onChange={event => onRemapColumn?.(field.id, event.target.value)} aria-label={`Column used for ${field.label}`} disabled={!onRemapColumn} className="w-full appearance-none truncate rounded-lg border border-ink/15 bg-paper py-1.5 pl-2.5 pr-8 text-[11px] font-medium text-ink focus:border-[#BB5E40] focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/20 disabled:opacity-60" data-id="element-3190" data-mp="2o7vdiysqof9b">
                          <option value="" data-id="element-3191" data-mp="2e9pgauhvnxas">Not used</option>
                          {analysis.headers.map(header => <option key={header} value={header} data-id="element-3192" data-mp="3umo0hnoric9z">{header}</option>)}
                        </select>
                        <ChevronDownIcon className="pointer-events-none absolute right-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-ink/40" aria-hidden="true" data-id="element-3193" data-mp="3pqp3jbpdblxy" />
                      </div>
                    </li>)}
                </ul>
              </div>
            </section>
          </div>

          <section className="mt-5 rounded-2xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-6" aria-labelledby="top-lines-heading" data-id="element-3194" data-mp="3o9s3pv97l6tn">
            <h2 id="top-lines-heading" className="text-sm font-semibold" data-id="element-3195" data-mp="kqgou7ewvdkp">Highest-value lines</h2>
            <div className="mt-4 -mx-2 overflow-x-auto px-2" data-id="element-3196" data-mp="pqbiujtzgj9z">
              <table className="w-full min-w-[560px] text-left text-xs" data-id="element-3197" data-mp="prmjwbw1ncxe">
                <thead className="border-b border-ink/10 text-ink/50" data-id="element-3198" data-mp="b0dwm9fm63nf">
                  <tr data-id="element-3199" data-mp="fyynhi8z7az7">
                    <th scope="col" className="pb-2 font-semibold" data-id="element-3200" data-mp="fyfi110iif22">Product</th>
                    <th scope="col" className="pb-2 font-semibold" data-id="element-3201" data-mp="13ojix0mbg0pd">Category</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-3202" data-mp="18gt82eflwwql">Units</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-3203" data-mp="301nmhr2tdqz8">MSRP</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-3204" data-mp="342zd9zxfci90">Est. recovery</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink/10" data-id="element-3205" data-mp="2qaj8ouovkc5z">
                  {analysis.topLines.map((line, index) => <tr key={`${line.sku}-${index}`} data-id="element-3206" data-mp="2v9450j10abp5" data-mpi={index}>
                      <th scope="row" className="py-3 pr-3 text-left font-medium text-ink" data-id="element-3207" data-mp="2gcwnjqgzfodk">
                        <span className="flex items-center gap-2.5" data-id="element-3208" data-mp="2lc97o3ggyhcg">
                          {line.image ? <img src={line.image} alt="" className="h-8 w-8 shrink-0 rounded object-cover" data-id="element-3209" data-mp="27di06zw43txa" /> : <span className="h-8 w-8 shrink-0 rounded bg-cream" aria-hidden="true" data-id="element-3210" data-mp="2cc300y7gcbl7" />}
                          <span className="min-w-0" data-id="element-3211" data-mp="akg4qkgj1uix">
                            <span className="block truncate" data-id="element-3212" data-mp="aj506if9ciqr">{line.name}</span>
                            {line.sku && <span className="block text-[10px] text-ink/45" data-id="element-3213" data-mp="kp6br4qsfou0">{line.sku}</span>}
                          </span>
                        </span>
                      </th>
                      <td className="py-3 pr-3 text-ink/65" data-id="element-3214" data-mp="tgyhj24va9s9">{line.category}</td>
                      <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-3215" data-mp="oq8dbs86gcz5">{line.units.toLocaleString()}</td>
                      <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-3216" data-mp="13e37ld1u2pec">{currency(line.msrp)}</td>
                      <td className="py-3 text-right font-semibold tabular-nums text-ink" data-id="element-3217" data-mp="ygkv5colxs1l">
                        {currency(line.msrp * line.units * analysis.recoveryRate)}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mt-5 rounded-2xl border border-[#BB5E40]/30 bg-[#BB5E40]/[0.05] p-5 sm:p-6" aria-labelledby="ask-lot-heading" data-id="element-3218" data-mp="3e7frypz63tb8">
            <div className="flex items-center gap-2.5" data-id="element-3219" data-mp="1d4ptecmqhyde">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cream" data-id="element-3220" data-mp="1d699o38e49f6">
                <CarliStars className="h-4 w-4" data-id="element-3221" data-mp="1i41bpq2r1bx3" />
              </span>
              <div data-id="element-3222" data-mp="1n1uf6bco3rto">
                <h2 id="ask-lot-heading" className="text-sm font-semibold" data-id="element-3223" data-mp="1n1ayexyr79h8">Ask Carli about this lot</h2>
                <p className="text-[11px] text-ink/55" data-id="element-3224" data-mp="1s3jptpvza3h8">She is reading this file alongside your live sales history.</p>
              </div>
            </div>

            {chat.length > 0 && <div className="mt-4 max-h-72 space-y-3 overflow-y-auto pr-1 [scrollbar-gutter:stable]" data-id="element-3225" data-mp="2112hve81hi2m">
                {chat.map((message, index) => <div key={index} className={message.role === 'user' ? 'flex justify-end' : 'flex justify-start'} data-id="element-3226" data-mp="2as6w2bpw4ywe" data-mpi={index}>
                    <p className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-xs leading-relaxed ${message.role === 'user' ? 'bg-[#BB5E40] text-cream' : 'bg-paper text-ink/75'}`} data-id="element-3227" data-mp="2fsci9cde5t73">
                      {message.content}
                    </p>
                  </div>)}
                {asking && <p className="flex items-center gap-2 text-xs text-ink/55" role="status" data-id="element-3228" data-mp="3vb0xjm4y7fcn">
                    <Loader2Icon className="h-3.5 w-3.5 animate-spin" aria-hidden="true" data-id="element-3229" data-mp="3qcpm56z08wsb" />
                    Carli is thinking…
                  </p>}
              </div>}

            <form onSubmit={event => {
            event.preventDefault();
            ask(question);
          }} className="mt-4 flex gap-2" data-id="element-3230" data-mp="mw0iptupy36v">
              <input value={question} onChange={event => setQuestion(event.target.value)} placeholder="How does this compare to what we have already sold?" aria-label="Ask Carli about this lot" className="min-w-0 flex-1 rounded-xl border border-[#BB5E40]/25 bg-paper px-3.5 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-[#BB5E40] focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/20" data-id="element-3231" data-mp="d4338awogp2c" />
              <button type="submit" disabled={!question.trim() || asking} className="shrink-0 rounded-xl bg-[#BB5E40] px-4 py-2.5 text-sm font-semibold text-cream transition-colors hover:bg-[#94452E] disabled:cursor-not-allowed disabled:bg-[#BB5E40]/55" data-id="element-3232" data-mp="genrla0qazv9">
                Ask
              </button>
            </form>

            {chat.length === 0 && <div className="mt-3 flex flex-wrap gap-2" data-id="element-3233" data-mp="zbyio7c7imzm">
                {['How does this compare to what we have already sold?', 'Which categories should we price hardest?', 'What would you change about the channel mix?'].map(prompt => <button key={prompt} type="button" onClick={() => ask(prompt)} className="rounded-full border border-[#BB5E40]/30 bg-paper px-3 py-1.5 text-[11px] font-semibold text-[#94452E] transition-colors hover:bg-[#BB5E40]/10" data-id="element-3234" data-mp="1wtfej7f3vex6">
                    {prompt}
                  </button>)}
              </div>}
          </section>
        </div>
      </motion.section>
    </div>;
}
function Outlook({
  label,
  value,
  note,
  highlight = false
}: {
  label: string;
  value: number;
  note: string;
  highlight?: boolean;
}) {
  return <div className={`rounded-xl border p-4 ${highlight ? 'border-[#BB5E40] bg-[#BB5E40]/[0.06]' : 'border-ink/10 bg-cream/45'}`} data-id="element-3235" data-mp="37kmtvqgaebvw">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-3236" data-mp="32mv2cxjk0rs8">{label}</p>
      <p className={`mt-2 text-xl font-semibold tabular-nums ${highlight ? 'text-[#94452E]' : 'text-ink'}`} data-id="element-3237" data-mp="mrrtg865y6c8">{currency(value)}</p>
      <p className="mt-1 text-[11px] leading-relaxed text-ink/55" data-id="element-3238" data-mp="3bmhq30thht18">{note}</p>
    </div>;
}
function Stat({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div data-id="element-3239" data-mp="3gnp1d9uu5acn">
      <dt className="text-[10px] font-semibold uppercase tracking-wider text-ink/45" data-id="element-3240" data-mp="3gnf91fmbir3s">{label}</dt>
      <dd className="mt-1 text-sm font-semibold tabular-nums text-ink" data-id="element-3241" data-mp="3klczelrtrj8r">{value}</dd>
    </div>;
}
function BreakdownCard({
  title,
  rows
}: {
  title: string;
  rows: Breakdown[];
}) {
  if (rows.length === 0) return null;
  const max = Math.max(...rows.map(row => row.units), 1);
  return <section className="rounded-2xl border border-ink/10 bg-paper p-5 shadow-sm" data-id="element-3242" data-mp="2wxv2kbtpduyx">
      <h2 className="text-sm font-semibold" data-id="element-3243" data-mp="31v51ci2zfizf">{title}</h2>
      <ul className="mt-4 space-y-3" data-id="element-3244" data-mp="36n4y61nr9vrs">
        {rows.map(row => <li key={row.label} data-id="element-3245" data-mp="36phj8not8m92">
            <div className="flex items-baseline justify-between gap-3 text-xs" data-id="element-3246" data-mp="298k9lgilh4vc">
              <span className="truncate font-medium text-ink/75" data-id="element-3247" data-mp="2e73wbj50ikor">{row.label}</span>
              <span className="shrink-0 tabular-nums text-ink/55" data-id="element-3248" data-mp="2e510ujl3bzcn">
                {row.units.toLocaleString()} · {Math.round(row.share)}%
              </span>
            </div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-cream" data-id="element-3249" data-mp="21p189u5hgoxy">
              <div className="h-full rounded-full bg-[#BB5E40]" style={{
            width: `${row.units / max * 100}%`
          }} data-id="element-3250" data-mp="1wodgh69fk42o" />
            </div>
          </li>)}
      </ul>
    </section>;
}